import { useCallback } from 'react';

const { applyCommand } = require('../../../shared/game/belot/armenian/engine');
const { legalPlays } = require('../../../shared/game/belot/armenian/tricks');
const { mapRoundScoredToSheetRow } = require('../../../game/belotArmenian/scoreSheet');
const { teamOfSeat } = require('../../../shared/game/belot/armenian/types');
const { diffSerializedEngineStates } = require('../../../game/belotArmenian/engineStateDiff');
const {
  inferTriggeredAnimationIds,
  inferValidationCode,
} = require('../../../game/belotArmenian/engineDebugUiHints');
const {
  serializeArmenianEngineStateForLog,
  serializeCommandForLog,
  serializeEventsForLog,
  serializeEventSummariesForLog,
} = require('../../../game/belotArmenian/debugSerialize');

const ENGINE_DEBUG_LOG_CAP = 500;
const SEAT_ACTION_TTL_MS = 3600;
const HUMAN_SEAT = 0;
/** Локальный армянский стол: восстановление лога торгов после bfcache / перезагрузки вкладки при том же раунде. */
const AUCTION_LOG_SESSION_KEY = 'seka:belotArm:auctionLog';

function persistLocalAuctionLogSnapshot(state, nextLog) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    if (state?.phase === 'auction') {
      const fp = `${Number(state.roundIndex) || 0}|${Number(state.dealerSeat) || 0}`;
      window.sessionStorage.setItem(
        AUCTION_LOG_SESSION_KEY,
        JSON.stringify({ fp, log: Array.isArray(nextLog) ? nextLog : [] }),
      );
    } else {
      window.sessionStorage.removeItem(AUCTION_LOG_SESSION_KEY);
    }
  } catch (_) {
    /* ignore quota / private mode */
  }
}

export function useBelotArmenianController({
  formatAuctionBid,
  extractRoundSummary,
  engineStateRef,
  setEngineState,
  setBanner,
  setLastSeatActions,
  setLastRoundSummary,
  setScoreSheetRows,
  setEngineDebugLog,
  hardcoreChoiceByKey,
  setHardcoreDeclDialogOpen,
  setAuctionSeatActions,
  setAuctionLog,
  onAuctionEnded,
  onPostApplyFx,
}) {
  const appendEngineDebug = useCallback((entry) => {
    setEngineDebugLog((log) => {
      const seq = log.length ? log[log.length - 1].seq + 1 : 1;
      const row = {
        seq,
        at: new Date().toISOString(),
        cmd: entry.cmd,
        events: entry.events,
        ...(entry.eventSummaries ? { eventSummaries: entry.eventSummaries } : {}),
        ...(entry.diff ? { diff: entry.diff } : {}),
        ...(entry.rng ? { rng: entry.rng } : {}),
        ...(entry.ui ? { ui: entry.ui } : {}),
        ...(entry.validation ? { validation: entry.validation } : {}),
        stateAfter: entry.stateAfter ?? null,
        ok: entry.ok !== false,
        ...(entry.error ? { error: entry.error } : {}),
      };
      const next = [...log, row];
      return next.length > ENGINE_DEBUG_LOG_CAP ? next.slice(-ENGINE_DEBUG_LOG_CAP) : next;
    });
  }, [setEngineDebugLog]);

  const afterEngineCommandApplied = useCallback(
    (cmd, prev, state, events) => {
      if (cmd?.type === 'START_ROUND') {
        setAuctionSeatActions({});
        setAuctionLog([]);
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(AUCTION_LOG_SESSION_KEY);
          }
        } catch (_) {
          /* ignore */
        }
      }

      const actionPatch = {};
      const auctionActionPatch = {};
      const now = Date.now();
      const seatIndex = (s) => {
        const n = Number(s);
        return Number.isInteger(n) && n >= 0 && n <= 3 ? n : null;
      };
      const markSeatAction = (seat, kind, label) => {
        const si = seatIndex(seat);
        if (si == null) return;
        actionPatch[si] = { kind, label, at: now };
      };
      const markAuctionSeatAction = (seat, kind, label, extra = {}) => {
        const si = seatIndex(seat);
        if (si == null) return;
        auctionActionPatch[si] = { kind, label, at: now, ...extra };
      };
      const isAuctionDecision =
        cmd?.type === 'PASS' || cmd?.type === 'BID' || cmd?.type === 'CONTRA' || cmd?.type === 'RECONTRA';
      if (cmd?.type === 'PASS') markAuctionSeatAction(cmd.seat, 'pass', 'Пас');
      if (cmd?.type === 'BID') {
        const bidLabel = formatAuctionBid(cmd.bid) || '—';
        markAuctionSeatAction(cmd.seat, 'bid', bidLabel, { bid: cmd.bid });
      }
      if (cmd?.type === 'CONTRA') markAuctionSeatAction(cmd.seat, 'contra', 'Контра');
      if (cmd?.type === 'RECONTRA') markAuctionSeatAction(cmd.seat, 'recontra', 'Реконтра');
      if (cmd?.type === 'PLAY_CARD') markSeatAction(cmd.seat, 'play', 'Ход');
      for (const ev of events || []) {
        if (ev?.type === 'TRICK_WON') {
          markSeatAction(ev?.payload?.winnerSeat, 'trick', 'Взял взятку');
        }
      }
      /* Пишем действие торгов сразу по команде: UI-капля должна обновляться в тот же тик,
       * даже если phase-переходы/батчинг React происходят рядом. */
      if (Object.keys(auctionActionPatch).length > 0) {
        setAuctionSeatActions((prevMap) => ({ ...prevMap, ...auctionActionPatch }));
      }
      const seatForLog = seatIndex(cmd?.seat);
      const hadAuctionLogDecision =
        prev.phase === 'auction' && isAuctionDecision && seatForLog != null;
      const leavesAuctionPhase = prev.phase === 'auction' && state.phase !== 'auction';
      const auctionRedeal = Array.isArray(events) && events.some((e) => e?.type === 'AUCTION_REDEAL');
      if (auctionRedeal) {
        setAuctionSeatActions({});
      }
      /* Один setAuctionLog: иначе в одном тике append + setAuctionLog([]) при redeal/выходе из торгов
       * батчатся и итог — пустой лог, «капли» не появляются. */
      if (hadAuctionLogDecision || leavesAuctionPhase || auctionRedeal) {
        setAuctionLog((prevLog) => {
          let next;
          if (auctionRedeal || leavesAuctionPhase) {
            next = [];
          } else if (hadAuctionLogDecision) {
            const label =
              cmd.type === 'PASS'
                ? 'Пас'
                : cmd.type === 'BID'
                  ? formatAuctionBid(cmd.bid) || '—'
                  : cmd.type === 'CONTRA'
                    ? 'Контра'
                    : 'Реконтра';
            next = [
              ...prevLog,
              {
                id: `${now}-${seatForLog}-${cmd.type}-${prevLog.length}`,
                at: now,
                seat: seatForLog,
                kind: cmd.type.toLowerCase(),
                label,
                ...(cmd.type === 'BID' && cmd.bid && typeof cmd.bid === 'object' ? { bid: cmd.bid } : {}),
              },
            ];
          } else {
            return prevLog;
          }
          persistLocalAuctionLogSnapshot(state, next);
          return next;
        });
      }
      if (typeof onAuctionEnded === 'function') {
        const auctionEndedEvent = Array.isArray(events)
          ? events.find((e) => e?.type === 'AUCTION_ENDED')
          : null;
        if (process.env.NODE_ENV !== 'production') {
          const eventTypes = Array.isArray(events) ? events.map((e) => e?.type).filter(Boolean) : [];
          console.debug('[belot] auction close path', {
            cmdType: cmd?.type || null,
            eventTypes,
            prevPhase: prev?.phase || null,
            nextPhase: state?.phase || null,
            hasContract: Boolean(state?.contract),
            hasAuctionEndedEvent: Boolean(auctionEndedEvent),
          });
        }
        if (auctionEndedEvent) {
          const contract = state?.contract || null;
          Promise.resolve().then(() =>
            onAuctionEnded({
              at: now,
              bid: auctionEndedEvent?.payload?.bid || null,
              bidAuthorSeat: auctionEndedEvent?.payload?.bidAuthorSeat ?? null,
              contractTargetPoints: contract?.contractTargetPoints ?? null,
              trump: contract?.trump ?? null,
              contraMultiplier: contract?.contraMultiplier ?? 1,
            }),
          );
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[belot] onAuctionEnded by AUCTION_ENDED', {
              bid: auctionEndedEvent?.payload?.bid || null,
              bidAuthorSeat: auctionEndedEvent?.payload?.bidAuthorSeat ?? null,
              contractTargetPoints: contract?.contractTargetPoints ?? null,
              trump: contract?.trump ?? null,
              contraMultiplier: contract?.contraMultiplier ?? 1,
            });
          }
        }
        const transitionedToDeclareWithContract =
          prev?.phase === 'auction' && state?.phase === 'declare' && state?.contract;
        if (!auctionEndedEvent && transitionedToDeclareWithContract) {
          const contract = state.contract;
          Promise.resolve().then(() =>
            onAuctionEnded({
              at: now,
              bid: state?.auction?.highestBid ?? null,
              bidAuthorSeat: contract?.bidAuthorSeat ?? null,
              contractTargetPoints: contract?.contractTargetPoints ?? null,
              trump: contract?.trump ?? null,
              contraMultiplier: contract?.contraMultiplier ?? 1,
            }),
          );
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[belot] onAuctionEnded by fallback auction->declare', {
              bid: state?.auction?.highestBid ?? null,
              bidAuthorSeat: contract?.bidAuthorSeat ?? null,
              contractTargetPoints: contract?.contractTargetPoints ?? null,
              trump: contract?.trump ?? null,
              contraMultiplier: contract?.contraMultiplier ?? 1,
            });
          }
        }
      }
      if (Object.keys(actionPatch).length > 0) {
        Promise.resolve().then(() =>
          setLastSeatActions((prevMap) => {
            const next = { ...prevMap, ...actionPatch };
            for (const k of Object.keys(next)) {
              const entry = next[k];
              if (!entry?.at || now - entry.at > SEAT_ACTION_TTL_MS) delete next[k];
            }
            return next;
          }),
        );
      }

      const summary = extractRoundSummary(prev, state, events);
      if (summary) {
        Promise.resolve().then(() => setLastRoundSummary(summary));
      }

      const roundEv = (Array.isArray(events) ? events : []).find((e) => e.type === 'ROUND_SCORED');
      if (
        roundEv &&
        roundEv.payload &&
        typeof roundEv.payload === 'object' &&
        Array.isArray(roundEv.payload.delta) &&
        state.teamScores &&
        typeof state.roundIndex === 'number'
      ) {
        const d = roundEv.payload.delta;
        const heroTeam = teamOfSeat(HUMAN_SEAT);
        const row = mapRoundScoredToSheetRow(
          /** @type {0|1} */ (heroTeam),
          [Number(d[0]) || 0, Number(d[1]) || 0],
          [Number(state.teamScores[0]) || 0, Number(state.teamScores[1]) || 0],
          state.roundIndex,
        );
        queueMicrotask(() => setScoreSheetRows((r0) => [...r0, row]));
      }
      Promise.resolve().then(() => setBanner(''));
      const prevSer = serializeArmenianEngineStateForLog(prev);
      const nextSer = serializeArmenianEngineStateForLog(state);
      const eventsArr = Array.isArray(events) ? events : [];
      const serializedEvents = serializeEventsForLog(eventsArr);
      const eventSummaries = serializeEventSummariesForLog(eventsArr);
      const diff = diffSerializedEngineStates(
        /** @type {Record<string, unknown>|null} */ (prevSer),
        /** @type {Record<string, unknown>|null} */ (nextSer),
      );
      /** @type {Record<string, unknown>|undefined} */
      let validation;
      if (cmd?.type === 'PLAY_CARD' && prev?.phase === 'play' && prev.contract && Array.isArray(prev.hands?.[cmd.seat])) {
        try {
          const ids = legalPlays(
            prev.hands[cmd.seat],
            prev.currentTrick || [],
            prev.contract.trumpSuit,
            prev.trickLeadSeat ?? 0,
          );
          validation = { isLegal: true, legalMovesSnapshot: ids };
        } catch {
          validation = { isLegal: true, legalMovesSnapshot: [] };
        }
      }
      const rm = state?.rngMeta;
      const rngLog =
        rm && typeof rm === 'object'
          ? {
              seed: rm.seed >>> 0,
              stateAfterStep: rm.draws,
              algorithm: 'mulberry32',
            }
          : { note: 'custom_rng' };
      queueMicrotask(() =>
        appendEngineDebug({
          cmd: serializeCommandForLog(cmd),
          events: serializedEvents,
          eventSummaries,
          diff,
          rng: rngLog,
          ui: { triggeredAnimationIds: inferTriggeredAnimationIds(cmd, eventsArr) },
          ...(validation ? { validation } : {}),
          stateAfter: null,
          ok: true,
        }),
      );
    },
    [
      appendEngineDebug,
      extractRoundSummary,
      formatAuctionBid,
      setAuctionLog,
      setAuctionSeatActions,
      setBanner,
      setLastRoundSummary,
      setLastSeatActions,
      setScoreSheetRows,
      onAuctionEnded,
    ],
  );

  const apply = useCallback(
    (cmd) => {
      const now = Date.now();
      const cmdSeat = Number(cmd?.seat);
      const hasSeat = Number.isInteger(cmdSeat) && cmdSeat >= 0 && cmdSeat <= 3;
      if (hasSeat) {
        if (cmd?.type === 'PASS') {
          setAuctionSeatActions((prevMap) => ({
            ...prevMap,
            [cmdSeat]: { kind: 'pass', label: 'Пас', at: now },
          }));
        } else if (cmd?.type === 'BID') {
          const bidLabel = formatAuctionBid(cmd?.bid) || '—';
          setAuctionSeatActions((prevMap) => ({
            ...prevMap,
            [cmdSeat]: { kind: 'bid', label: bidLabel, at: now, bid: cmd?.bid || null },
          }));
        } else if (cmd?.type === 'CONTRA') {
          setAuctionSeatActions((prevMap) => ({
            ...prevMap,
            [cmdSeat]: { kind: 'contra', label: 'Контра', at: now },
          }));
        } else if (cmd?.type === 'RECONTRA') {
          setAuctionSeatActions((prevMap) => ({
            ...prevMap,
            [cmdSeat]: { kind: 'recontra', label: 'Реконтра', at: now },
          }));
        }
      }
      const prev = engineStateRef?.current;
      if (!prev) return;
      /** @type {{ prev: unknown, state: unknown, events: unknown[] } | { prev: unknown, error: string } | null} */
      let outcome = null;
      try {
        const { state, events } = applyCommand(prev, cmd);
        outcome = { prev, state, events };
        engineStateRef.current = state;
        setEngineState(state);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        outcome = { prev, error: msg };
      }
      if (!outcome) return;
      if ('error' in outcome && outcome.error) {
        Promise.resolve().then(() => setBanner(outcome.error));
        const errMsg = outcome.error;
        const code = inferValidationCode(errMsg);
        const rm = prev?.rngMeta;
        const rngLog =
          rm && typeof rm === 'object'
            ? {
                seed: rm.seed >>> 0,
                stateAfterStep: rm.draws,
                algorithm: 'mulberry32',
              }
            : { note: 'custom_rng' };
        queueMicrotask(() =>
          appendEngineDebug({
            cmd: serializeCommandForLog(cmd),
            events: serializeEventsForLog([]),
            eventSummaries: [],
            diff: null,
            rng: rngLog,
            ui: { triggeredAnimationIds: inferTriggeredAnimationIds(cmd, []) },
            validation: { isLegal: false, code, detail: errMsg },
            stateAfter: null,
            ok: false,
            error: errMsg,
          }),
        );
        return;
      }
      afterEngineCommandApplied(cmd, outcome.prev, outcome.state, outcome.events);
      if (typeof onPostApplyFx === 'function') {
        try {
          onPostApplyFx({
            cmd,
            prev: outcome.prev,
            state: outcome.state,
            events: Array.isArray(outcome.events) ? outcome.events : [],
          });
        } catch {
          /* ignore FX hook */
        }
      }
    },
    [
      afterEngineCommandApplied,
      appendEngineDebug,
      engineStateRef,
      formatAuctionBid,
      onPostApplyFx,
      setAuctionSeatActions,
      setBanner,
      setEngineState,
    ],
  );

  /** @param {string[]|undefined} explicitDeclaredKeys — если передан массив, игнорируем `hardcoreChoiceByKey` */
  const confirmHardcoreDeclarations = useCallback((explicitDeclaredKeys) => {
    const keys =
      explicitDeclaredKeys != null && Array.isArray(explicitDeclaredKeys)
        ? explicitDeclaredKeys.filter((k) => typeof k === 'string' && k.length > 0)
        : Object.entries(hardcoreChoiceByKey || {})
            .filter(([, v]) => v)
            .map(([k]) => k);
    apply({ type: 'COMMIT_FIRST_PLAY_DECLARATIONS', seat: HUMAN_SEAT, payload: { declaredKeys: keys } });
    setHardcoreDeclDialogOpen(false);
  }, [apply, hardcoreChoiceByKey, setHardcoreDeclDialogOpen]);

  return {
    apply,
    appendEngineDebug,
    afterEngineCommandApplied,
    confirmHardcoreDeclarations,
  };
}

